import { Injectable } from '@angular/core';
import { SelectionAction, ExportSelection } from 'gung-list';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap } from 'rxjs';
import { Customer } from '../../models';
import { AuthService } from '../auth/auth.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { NavbarConfigService } from '../navbar-config/navbar-config.service';
import { DocumentTemplatesService } from '../document-templates/document-templates.service';
import { CustomerExportService } from '../customer-export/customer-export.service';
import { Template } from 'gung-common';

@Injectable({
  providedIn: 'root'
})
export class CustomerSelectionActionConfigService {
  enableCustomerActivities = this.navbarConfigService.enableCustomerActivities;

  constructor(
    protected customerExportService: CustomerExportService,
    protected authService: AuthService,
    protected navbarConfigService: NavbarConfigService,
    protected gungModalService: GungModalService,
    protected documentTemplatesService: DocumentTemplatesService
  ) { }

  getSelectionActions(): Observable<SelectionAction<Customer>[]> {
    return this.authService.getRoles().pipe(
      first(),
      map(roles => {
        let userActions: SelectionAction<Customer>[] = [];
        if (this.enableCustomerActivities) {
          userActions.push({
            label: 'CREATE_ACTIVITIES',
            performAction: (selection: ExportSelection<Customer>) => {
              const selectedCustomers: Customer[] = Object.values(selection.selectedItems);
              if(selectedCustomers.length > 1) {
                this.gungModalService.openCreateActivityModal(undefined, undefined, undefined, selectedCustomers).then(
                  result => {},
                  reason => {
                    console.log(`Dismissed ${reason}`);
                  }
                );
              } else {
                this.gungModalService.openCreateActivityModal(undefined, undefined, selectedCustomers[0].id).then(
                  result => {},
                  reason => {
                    console.log(`Dismissed ${reason}`);
                  }
                );
              }

              return of();
            }
          });
        }
        return userActions;
      })
    );
  }

  getDynamicSelectionActions(): Observable<SelectionAction<Customer>[]> {
    return forkJoin([
      this.authService.getRoles().pipe(first()),
      this.documentTemplatesService.getTemplates().pipe(first(), catchError(() => of([])))
    ]).pipe(
      first(),
      mergeMap(([roles, templates]) => {
        const dynamicActions: SelectionAction<Customer>[] = [];
        const customerTemplates = templates.filter(t => t.type === 'CUSTOMER');
        templates.forEach(template => {
          if (template.type === 'CUSTOMER' && template.showXlsxAutomatically && this.matchesAnyUserRole(template, roles)) {
            dynamicActions.push(this.getExportActionFromTemplate(template));
          }
        });
        return of(dynamicActions);
      })
    );
  }

  matchesAnyUserRole(template: Template, roles: string[]): boolean {
    if (!roles || !template.availableToRoles) {
      return false;
    }
    return roles.some(role => template.availableToRoles.includes(role));
  }

  getExportActionFromTemplate(template: Template): SelectionAction<Customer> {
    return {
      label: template.name,
      performAction: (selection: ExportSelection<Customer>) => {
        const customerList: Customer[] = Object.values(selection.selectedItems);
        this.customerExportService.exportExcel(customerList, template.id).pipe(first()).subscribe();
        return of();
      }
    };
  }
}
